import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import type {MemberUserSerializer} from "@/generated/DjangoTypes";
import {useLogin} from "@/hooks/useLogin";
import {client} from "@/httpClient";
import UserAvatar from "@/pages/profile/UserAvatar";
import CredentialsStep from "@/pages/registerCompany/steps/CredentialsStep";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {PERMISSION_LABEL_KEYS} from "@/store/constants";
import {isRegisterCompanyMemberModalOpenAtom} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import type {TPermission} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {useSearch} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useShallow} from "zustand/shallow";

const RegisterCompanyMemberModal: FC = () => {
  const {t} = useTranslation(["multi_account"]);
  const {agency_id: agencyIdFromUrl, company_id: companyIdFromUrl, permission, token} = useSearch({strict: false});
  const setIsModalOpen = useSetAtom(isRegisterCompanyMemberModalOpenAtom);
  const {login, isPending: isLoggingIn} = useLogin();
  const validateCredentials = useRegistrationStore(state => state.validateCredentials);
  const [email, firstName, lastName, password] = useRegistrationStore(
    useShallow(state => [state.email, state.firstName, state.lastName, state.password]),
  );
  const [profilePicture, setProfilePicture] = useState<File | null>(null);

  const isAgencyInvite = !!agencyIdFromUrl;
  const invitedPermission = permission as TPermission | undefined;

  const {mutate: registerMember, isPending: isRegistering} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: isAgencyInvite
      ? AGENCY_KEYS.mAgencyMemberRegistration(Number(agencyIdFromUrl))
      : COMPANY_KEYS.mCompanyMemberRegistration(Number(companyIdFromUrl)),
    mutationFn: () => {
      const formData = new FormData();

      formData.append("first_name", firstName);
      formData.append("last_name", lastName);
      formData.append("email", email);
      formData.append("password", password);
      formData.append("username", email);
      formData.append("token", token!);

      if (profilePicture) {
        formData.append("profile_picture", profilePicture);
      }

      return client.post<MemberUserSerializer>(
        isAgencyInvite
          ? AGENCY_KEYS.mAgencyMemberRegistration(Number(agencyIdFromUrl))[0]
          : COMPANY_KEYS.mCompanyMemberRegistration(Number(companyIdFromUrl))[0],
        formData,
        {headers: {"Content-Type": "multipart/form-data"}},
      );
    },
    onSuccess: response => {
      capturePostHogEvent(PostHogEvents.InvitedUserRegistrationFinished, {
        agencyId: isAgencyInvite && !!agencyIdFromUrl ? +agencyIdFromUrl : undefined,
        companyId: !isAgencyInvite && !!companyIdFromUrl ? +companyIdFromUrl : undefined,
        invitedBy: isAgencyInvite ? "agency" : "company",
        userId: response.data.id,
      });
      login({email, password});
      setIsModalOpen(false);
    },
  });

  return (
    <Modal
      backgroundClassName="min-h-[80vh] max-w-190 [&>div]:border-0 lg:min-h-175"
      customBottomRow={<></>}
      customTitleRow={<div className="h-16" />}
      onClose={() => setIsModalOpen(false)}
      shouldHideCloseButton>
      <div className="flex flex-col gap-14">
        <div className="flex flex-col items-center gap-5">
          <span className="text-neutral-black text-xl font-semibold">{t("multi_account:add_profile_photo")}</span>
          <UserAvatar handleUpload={setProfilePicture} />
          {!isAgencyInvite && invitedPermission && (
            <div className="flex flex-col items-center gap-3">
              <span className="text-neutral-grey-dark text-sm font-medium">{t("multi_account:permission_type")}</span>
              <div className="border-primary-light-solid text-neutral-black w-fit rounded-md border px-4 py-2 text-sm">
                {t(PERMISSION_LABEL_KEYS[invitedPermission])}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <CredentialsStep className="gap-3" showCompanyName={false} showOnboardingCta={false} />
          <Button
            buttonStyle="Gradient"
            className="w-fit px-8 py-4"
            dataTestId="account-edit-submit-button"
            isDisabled={!email || !firstName || !lastName || !password || isRegistering || isLoggingIn}
            isLoading={isRegistering || isLoggingIn}
            onClick={() => validateCredentials() && registerMember()}>
            {t("multi_account:create_account")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RegisterCompanyMemberModal;
